/**
 * Workspace IO Context Implementation
 *
 * Connects the calcscript evaluator's file operations (save/load/csvread/
 * csvwrite/fileread/filewrite/dir/delete/exist/importdata/uigetfile) to the
 * browser workspace: a real local folder via the File System Access API, or a
 * localStorage-backed virtual folder when that API is unavailable. See
 * store/workspaceStore.ts. No backend involved.
 */

import type {
  WorkspaceIOContext,
  CalcValue,
  CalcMatrix,
} from "@quanta/calcscript";
import {
  wsReadText,
  wsWriteText,
  wsList,
  wsRemove,
  wsExists,
} from "../store/workspaceStore";

type MatlabValue = CalcValue;
type MatlabMatrix = CalcMatrix;

function matrixToCsv(data: MatlabMatrix): string {
  return data
    .map((row: MatlabValue | MatlabValue[]) =>
      Array.isArray(row) ? row.join(",") : String(row),
    )
    .join("\n");
}

function csvToMatrix(text: string, rowOffset = 0, colOffset = 0): MatlabMatrix {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.slice(rowOffset).map((line) =>
    line
      .split(",")
      .slice(colOffset)
      .map((cell) => {
        const num = parseFloat(cell.trim());
        return isNaN(num) ? 0 : num;
      }),
  ) as MatlabMatrix;
}

/**
 * Create a workspace IO context for file operations.
 */
export function createWorkspaceIOContext(
  sessionId: string | null,
): WorkspaceIOContext {
  const getSessionId = () => sessionId;

  return {
    getSessionId,

    async save(
      filename: string,
      variables: Record<string, MatlabValue>,
      options?: { append?: boolean },
    ) {
      try {
        let merged = variables;
        if (options?.append) {
          const existing = await wsReadText(filename);
          if (existing) {
            try {
              merged = { ...(JSON.parse(existing) as Record<string, MatlabValue>), ...variables };
            } catch {
              merged = variables;
            }
          }
        }
        await wsWriteText(filename, JSON.stringify(merged, null, 2));
        return {
          success: true,
          message: `Saved ${Object.keys(variables).length} variable(s) to '${filename}'`,
        };
      } catch (error) {
        return {
          success: false,
          message: error instanceof Error ? error.message : "Failed to save",
        };
      }
    },

    async load(filename: string, variableNames?: string[]) {
      const text = await wsReadText(filename);
      if (text === null) throw new Error(`File '${filename}' not found`);
      let data: Record<string, MatlabValue>;
      try {
        data = JSON.parse(text) as Record<string, MatlabValue>;
      } catch {
        throw new Error(`File '${filename}' is not a saved variable file`);
      }
      let variables = data;
      if (variableNames) {
        variables = {} as Record<string, MatlabValue>;
        for (const name of variableNames) {
          if (name in data) variables[name] = data[name];
        }
      }
      return { variables, variableNames: Object.keys(variables) };
    },

    async csvread(filename: string, rowOffset = 0, colOffset = 0) {
      const text = await wsReadText(filename);
      if (text === null) throw new Error(`File '${filename}' not found`);
      // A file written by save() is JSON with a `data` field; otherwise raw CSV.
      try {
        const parsed = JSON.parse(text) as { data?: unknown };
        if (parsed && Array.isArray(parsed.data)) {
          return (parsed.data as MatlabMatrix)
            .slice(rowOffset)
            .map((row) => (Array.isArray(row) ? row.slice(colOffset) : [row]));
        }
      } catch {
        /* not JSON, so treat as CSV text */
      }
      return csvToMatrix(text, rowOffset, colOffset);
    },

    async csvwrite(
      filename: string,
      data: MatlabMatrix,
      _rowOffset = 0,
      _colOffset = 0,
    ) {
      await wsWriteText(filename, matrixToCsv(data));
      return { success: true };
    },

    async fileread(filename: string) {
      const text = await wsReadText(filename);
      if (text === null) throw new Error(`File '${filename}' not found`);
      return text;
    },

    async filewrite(filename: string, content: string) {
      await wsWriteText(filename, content);
      return { success: true };
    },

    async dir(pattern?: string) {
      let files = await wsList();
      if (pattern && pattern !== "*") {
        const regex = new RegExp(
          "^" + pattern.replace(/\*/g, ".*").replace(/\?/g, ".") + "$",
          "i",
        );
        files = files.filter((f) => regex.test(f.name));
      }
      return files.map((f) => ({
        name: f.name,
        isdir: false,
        bytes: f.size,
        date: "",
      }));
    },

    async delete(filename: string) {
      await wsRemove(filename);
      return { success: true };
    },

    async exist(name: string, type?: string) {
      if (type && type !== "file") return 0;
      return (await wsExists(name)) ? 2 : 0;
    },

    async importdata(filename: string) {
      const text = await wsReadText(filename);
      if (text === null) throw new Error(`File '${filename}' not found`);
      // Try JSON first (files written by save()).
      try {
        const parsed = JSON.parse(text);
        return { data: parsed as MatlabValue };
      } catch {
        /* fall through to CSV parsing */
      }
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const firstRow = lines[0]?.split(",") || [];
      const hasHeaders = firstRow.some((cell) => isNaN(parseFloat(cell.trim())));
      const startRow = hasHeaders ? 1 : 0;
      const data = lines.slice(startRow).map((line) =>
        line.split(",").map((cell) => {
          const num = parseFloat(cell.trim());
          return isNaN(num) ? cell.trim() : num;
        }),
      );
      return {
        data: data as unknown as MatlabValue,
        textdata: hasHeaders ? [firstRow] : undefined,
        colheaders: hasHeaders ? firstRow : undefined,
      };
    },

    async uigetfile(_filter?: string, _title?: string) {
      return new Promise<{ filename: string; path: string; content: string } | null>(
        (resolve) => {
          const input = document.createElement("input");
          input.type = "file";
          if (_filter) {
            if (_filter.includes("mat")) input.accept = ".mat,.json";
            else if (_filter.includes("csv")) input.accept = ".csv";
            else if (_filter.includes("txt")) input.accept = ".txt";
            else input.accept = _filter;
          }
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return resolve(null);
            resolve({ filename: file.name, path: file.name, content: await file.text() });
          };
          input.oncancel = () => resolve(null);
          input.click();
        },
      );
    },

    async uiputfile(_filter?: string, _title?: string, defaultName?: string) {
      return {
        filename: defaultName || "untitled.mat",
        path: defaultName || "untitled.mat",
      };
    },
  };
}

/**
 * Handle async operation results from the evaluator.
 * Maps asyncOp markers to the IO context methods above.
 */
export async function handleAsyncOperation(
  operation: {
    type_: "asyncOp";
    operation: string;
    [key: string]: unknown;
  },
  ioContext: WorkspaceIOContext,
  _setVariable: (name: string, value: MatlabValue) => void,
): Promise<{
  result: MatlabValue;
  message: string;
  variablesToSet?: Record<string, MatlabValue>;
}> {
  switch (operation.operation) {
    case "save": {
      if (!ioContext.save) return { result: 0, message: "Save not available" };
      const filename = (operation.filename as string) || "workspace.mat";
      const variables = (operation.variables || {}) as Record<string, MatlabValue>;
      const saveResult = await ioContext.save(filename, variables);
      const varNames = Object.keys(variables);
      return {
        result: saveResult.success ? 1 : 0,
        message: saveResult.success
          ? `Saved ${varNames.length} variable(s) to '${filename}': ${varNames.join(", ")}`
          : saveResult.message,
      };
    }
    case "load": {
      if (!ioContext.load) return { result: 0, message: "Load not available" };
      const filename = (operation.filename as string) || "";
      const variableNames = (operation.variableNames as string[] | null) || undefined;
      try {
        const loadResult = await ioContext.load(filename, variableNames);
        return {
          result: {
            type_: "loadResult",
            filename,
            variableNames: loadResult.variableNames,
          } as unknown as MatlabValue,
          message: `Loaded ${loadResult.variableNames.length} variable(s) from '${filename}': ${loadResult.variableNames.join(", ")}`,
          variablesToSet: loadResult.variables,
        };
      } catch (error) {
        return {
          result: 0,
          message: error instanceof Error ? error.message : `Failed to load '${filename}'`,
        };
      }
    }
    case "csvread": {
      if (!ioContext.csvread) return { result: [], message: "CSV read not available" };
      const filename = (operation.filename as string) || "";
      const rowOffset = (operation.rowOffset as number) || 0;
      const colOffset = (operation.colOffset as number) || 0;
      try {
        const data = await ioContext.csvread(filename, rowOffset, colOffset);
        return { result: data, message: `Read ${data.length} rows from '${filename}'` };
      } catch (error) {
        return {
          result: [],
          message: error instanceof Error ? error.message : `Failed to read '${filename}'`,
        };
      }
    }
    case "csvwrite": {
      if (!ioContext.csvwrite) return { result: 0, message: "CSV write not available" };
      const filename = (operation.filename as string) || "";
      const data = (operation.data || []) as MatlabMatrix;
      const rowOffset = (operation.rowOffset as number) || 0;
      const colOffset = (operation.colOffset as number) || 0;
      try {
        await ioContext.csvwrite(filename, data, rowOffset, colOffset);
        return { result: 1, message: `Wrote data to '${filename}'` };
      } catch (error) {
        return {
          result: 0,
          message: error instanceof Error ? error.message : `Failed to write '${filename}'`,
        };
      }
    }
    case "dir": {
      if (!ioContext.dir) return { result: [], message: "Directory listing not available" };
      const pattern = (operation.pattern as string) || "*";
      try {
        const files = await ioContext.dir(pattern);
        return {
          result: files.map((f) => ({
            name: f.name,
            isdir: f.isdir ? 1 : 0,
            bytes: f.bytes,
            date: f.date,
          })),
          message: `Found ${files.length} file(s)`,
        };
      } catch (error) {
        return {
          result: [],
          message: error instanceof Error ? error.message : "Failed to list files",
        };
      }
    }
    case "fileread": {
      if (!ioContext.fileread) return { result: "", message: "File read not available" };
      const filename = (operation.filename as string) || "";
      try {
        return { result: await ioContext.fileread(filename), message: "" };
      } catch (error) {
        return {
          result: "",
          message: error instanceof Error ? error.message : `Failed to read '${filename}'`,
        };
      }
    }
    case "importdata": {
      if (!ioContext.importdata) return { result: null, message: "Import data not available" };
      const filename = (operation.filename as string) || "";
      try {
        const result = await ioContext.importdata(filename);
        return { result: result.data, message: "" };
      } catch (error) {
        return {
          result: null,
          message: error instanceof Error ? error.message : `Failed to import '${filename}'`,
        };
      }
    }
    case "uigetfile": {
      if (!ioContext.uigetfile)
        return { result: "", message: "Open file not available" };
      const filter = (operation.filter as string) || undefined;
      const title = (operation.title as string) || undefined;
      try {
        const picked = await ioContext.uigetfile(filter, title);
        if (!picked) return { result: "", message: "No file selected" };
        // Copy the chosen file into the workspace so the calcscript file verbs
        // (load, fileread, csvread, importdata) can read it afterwards.
        await wsWriteText(picked.filename, picked.content);
        return {
          result: picked.filename,
          message: `Opened '${picked.filename}'. Read it with load, fileread or csvread.`,
        };
      } catch (error) {
        return {
          result: "",
          message: error instanceof Error ? error.message : "Failed to open file",
        };
      }
    }
    case "exist": {
      if (!ioContext.exist) return { result: 0, message: "" };
      const name = (operation.name as string) || "";
      const checkType = (operation.checkType as string) || "any";
      try {
        return { result: await ioContext.exist(name, checkType), message: "" };
      } catch {
        return { result: 0, message: "" };
      }
    }
    default:
      return { result: 0, message: `Unknown operation: ${operation.operation}` };
  }
}
